import axios from "axios";
import environment from "environment";
import { forceLogout } from "utilities/HttpUtility";
import { getCsrfToken } from "utilities/authSession";

/**
 * Centralized API client for the dashboard/document endpoints.
 *
 * The pages used to hand-roll axios calls with copy-pasted header building
 * and a duplicated 401/force-logout block in every catch. New batch-2 code
 * paths go through this client so auth headers and session-expiry handling
 * live in exactly one place.
 */

const buildHeaders = (extra = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...extra,
  };
  const token = sessionStorage.getItem("authToken");
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const csrf = getCsrfToken();
  if (csrf) headers["X-CSRF-Token"] = csrf;
  return headers;
};

const handleError = (err) => {
  const status = err?.response?.status;
  if (status === 401) {
    // Single-flight: parallel 401s must not each open a toast.
    forceLogout(status);
  }
  // Re-throw a normalized error so callers can show a translated toast.
  // Callers should skip their own UI for status === 401.
  const normalizedError = new Error(
    err?.response?.data?.message || err?.message || "requestFailed"
  );
  normalizedError.status = status;
  normalizedError.raw = err;
  throw normalizedError;
};

const request = async (
  method,
  path,
  { params, data, headers: extraHeaders, responseType, signal } = {}
) => {
  try {
    const headers = buildHeaders(extraHeaders);
    if (responseType === "arraybuffer") {
      delete headers["Content-Type"];
      headers.Accept = "*/*";
    }
    const response = await axios({
      method,
      url: `${environment.baseApi}${path}`,
      params,
      data,
      responseType,
      headers,
      withCredentials: true,
      signal,
    });
    return responseType ? response : response?.data;
  } catch (err) {
    handleError(err);
    return null; // unreachable; handleError always throws
  }
};

const apiClient = {
  get: (path, params) => request("get", path, { params }),
  getArrayBuffer: (path, params, options = {}) =>
    request("get", path, {
      params,
      responseType: "arraybuffer",
      signal: options.signal,
    }),
  post: (path, data, params, headers) =>
    request("post", path, { data, params, headers }),
  patch: (path, data, params) => request("patch", path, { data, params }),
  put: (path, data, params) => request("put", path, { data, params }),
};

export default apiClient;
